import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useSelectedStore } from '~/api/easy-estimator-api/stores-api/useSelectedStore';
import { LOCAL_STORAGE_KEY } from '~/core/constants';
import { useEquivalentProductMatching } from '~/core/hooks/useEquivalentProductMatching/useEquivalentProductMatching';
import {
  NotifierType,
  SimpleMessage,
  showNotification,
} from '~/legacy/components';
import { usePricedProductsContext } from '~/legacy/easy-estimator-page/context/pricedProductsContext';
import { useAppDispatch } from '~/redux/hooks';
import {
  updateEeStatusList,
  updateIgnoreFetchedEstimate,
  updateIsNewEstimate,
  updateLoadEstimateCode,
} from '~/redux/slices/estimate';
import { RouteEnum } from '~/redux/slices/route/route.types';

interface UseExpiredEstimateActionsReturn {
  openToNewEstimate: () => void;
  createCloneEstimate: (
    translationSection: 'EXPIRED' | 'CLOSED'
  ) => Promise<void>;
}

const useExpiredEstimateActions = (): UseExpiredEstimateActionsReturn => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { resetAllPricing } = usePricedProductsContext();
  const selectedStore = useSelectedStore();
  const { replaceProductsWithEquivalents } = useEquivalentProductMatching();

  const openToNewEstimate = useCallback(() => {
    const url = new URL(document.location.href);

    url.searchParams.delete(LOCAL_STORAGE_KEY.LOAD_ESTIMATE);
    url.pathname = RouteEnum.STORE;
    window.location.href = url.toString();
  }, []);

  const createCloneEstimate = useCallback(
    async (translationSection: 'EXPIRED' | 'CLOSED') => {
      // 1. Mark that we should ignore the fetched estimate
      dispatch(updateIgnoreFetchedEstimate(true));

      // 2. Clear the load estimate code (treat as new estimate)
      dispatch(updateLoadEstimateCode(undefined));

      // 3. Mark as new estimate (will save with new code)
      dispatch(updateIsNewEstimate(true));

      // 4. Clear status list (removes NO_SAVE, allows saving)
      dispatch(updateEeStatusList([]));

      // Re-resolve ancillary product IDs to the current store's catalog so the
      // cloned design does not persist foreign-catalog ids that price at $0.
      // Awaited so the reconcile completes before pricing reset and any save.
      if (selectedStore?.companyId) {
        await replaceProductsWithEquivalents({
          storeId: selectedStore.companyId,
        });
      }

      // 5. Force pricing recalculation with current material/color prices
      resetAllPricing();

      // 6. Update URL (remove loadEstimate param without page reload)
      const url = new URL(document.location.href);

      url.searchParams.delete(LOCAL_STORAGE_KEY.LOAD_ESTIMATE);
      window.history.replaceState({}, '', url.toString());

      // 7. Show follow-up notification
      const basePath = `NOTIFICATIONS.FETCH_ESTIMATE.${translationSection}.COPY_FOLLOWUP`;

      showNotification(
        NotifierType.WARNING,
        t(`${basePath}.TITLE`),
        <SimpleMessage
          description={t(`${basePath}.DESC`)}
          message={t(`${basePath}.MSG`)}
        />,
        '',
        0
      );
    },
    [
      dispatch,
      resetAllPricing,
      replaceProductsWithEquivalents,
      selectedStore?.companyId,
      t,
    ]
  );

  return { openToNewEstimate, createCloneEstimate };
};

export default useExpiredEstimateActions;
